import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CalendarClock, FileText, Layers, Mail, MessageSquareText, Phone, Radio, ScanLine, Server, Zap } from "lucide-react";
import { Reveal } from "@/components/flow/Reveal";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { tb } from "@/lib/brandify";
import { cn } from "@/lib/utils";
import { EYEBROW, H2, SUB } from "@/pages/home/SiteChrome";

/* ---------------------------------------------------------------------------
   Why Listed — the same week, drawn as time.
   One card, two rails on a 14-day axis. Today: seven touchpoints spread over
   weeks (contact form → call-back → site visit → PDF → scan → WhatsApp → typed
   into the PMS). With Listed: one record, two days, live — the rail simply
   stops. Rails draw in, markers land in order, the figures
   rise last. Transform/opacity only. Beneath: three proof points.
--------------------------------------------------------------------------- */

const DAYS = 14;
const pct = (day) => `${(day / DAYS) * 100}%`;

const TODAY = [
  { day: 1, icon: Mail, k: "inbox" },
  { day: 2, icon: Phone, k: "callback" },
  { day: 4, icon: CalendarClock, k: "visit" },
  { day: 6, icon: FileText, k: "pdf" },
  { day: 8, icon: ScanLine, k: "scan" },
  { day: 11, icon: MessageSquareText, k: "chat" },
  { day: 14, icon: Server, k: "typed" },
];
const LISTED = [
  { day: 1, icon: Zap, k: "enquiry" },
  { day: 2, icon: Radio, k: "live", end: true },
];
const PROOF = [
  { n: 1, icon: Zap },
  { n: 2, icon: Layers },
  { n: 3, icon: Server },
];

/* Motion: rails draw with the clock of the section reveal; each marker lands when the rail reaches it. */
const DRAW = 1.6; // s — the Today rail's full draw
const railV = (len) => ({ hidden: { scaleX: 0 }, show: { scaleX: len, transition: { duration: DRAW * len, ease: [0.22, 1, 0.36, 1] } } });
const markV = (at) => ({
  hidden: { opacity: 0, y: 6, scale: 0.7 },
  show: { opacity: 1, y: 0, scale: 1, transition: { delay: 0.05 + DRAW * at, duration: 0.5, ease: EASE } },
});
const labelV = (at) => ({ hidden: { opacity: 0, y: 4 }, show: { opacity: 1, y: 0, transition: { delay: 0.2 + DRAW * at, duration: 0.5, ease: EASE } } });
const figureV = (at) => ({ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { delay: 0.25 + DRAW * at, duration: 0.7, ease: EASE } } });

/** One rail on the shared 30-day axis (≥ lg). Labels alternate above / below so nothing collides. */
function Rail({ items, tone, end, testId }) {
  const listed = tone === "listed";
  const last = items[items.length - 1].day / DAYS;
  return (
    <div className="relative h-[168px]" data-testid={testId}>
      {/* axis */}
      <span aria-hidden className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-hairline" />
      {/* the drawn rail */}
      <motion.span aria-hidden variants={railV(last)} className={cn("absolute left-0 top-1/2 -translate-y-1/2 h-[3px] w-full origin-left rounded-full will-change-transform", listed ? "bg-lavender brand-tint" : "bg-ink/25")} />
      {items.map(({ day, icon: Icon, k }, i) => {
        const at = day / DAYS;
        const above = i % 2 === 0;
        return (
          <div key={k} className="absolute top-1/2 -translate-y-1/2" style={{ left: pct(day) }} data-testid={`${testId}-${k}`}>
            <motion.span variants={markV(at)} className={cn("relative z-[2] flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border will-change-transform", listed ? "border-transparent text-white bg-lavender brand-tint shadow-[0_10px_24px_-12px_rgba(0,0,0,0.45)]" : "border-hairline bg-surface text-ink/60")}>
              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              {k === "live" && <span aria-hidden className="absolute inset-0 rounded-full bg-lavender brand-tint pin-pulse opacity-60" />}
            </motion.span>
            <motion.span variants={labelV(at)} className={cn("absolute left-0 w-[140px] -translate-x-1/2 text-center", above ? "bottom-[calc(50%+22px)]" : "top-[calc(50%+22px)]")}>
              <span className={cn("block font-mono text-[10.5px] tabular-nums", listed ? "text-ink/55" : "text-ink/40")}>{t("l11.pb.tl.day", { n: day })}</span>
              <span className={cn("mt-0.5 block text-[12.5px] leading-[1.35] text-balance", listed ? "font-medium text-ink" : "text-ink/65")}>{t(`l11.pb.tl.${tone}.${k}`)}</span>
            </motion.span>
          </div>
        );
      })}
      {/* the rail's end note. Today: beneath the last marker (whose label sits above).
          Listed: the two markers sit close together, so the note continues the rail itself — on the axis, to the right. */}
      {end && (
        <motion.span variants={labelV(last)} className={cn("absolute inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 h-7 text-[12px] font-medium", listed ? "top-1/2 -translate-y-1/2 bg-[var(--accent-soft)] text-ink" : "top-[calc(50%+22px)] -translate-x-1/2 bg-fill text-ink/60")} style={{ left: listed ? `calc(${pct(items[items.length - 1].day)} + 34px)` : pct(items[items.length - 1].day) }} data-testid={`${testId}-end`}>
          {end}
        </motion.span>
      )}
    </div>
  );
}

/** The same rails, stacked vertically for narrow screens (equal spacing — the day is in the label). */
function RailStack({ items, tone, end, testId }) {
  const listed = tone === "listed";
  return (
    <ol className="relative" data-testid={testId}>
      <span aria-hidden className={cn("absolute left-[15px] top-4 bottom-4 w-px", listed ? "bg-lavender/40 brand-tint" : "bg-hairline")} />
      {items.map(({ day, icon: Icon, k }, i) => (
        <motion.li key={k} variants={markV(i / items.length / 2)} className="relative flex items-start gap-3 py-2 will-change-transform" data-testid={`${testId}-${k}`}>
          <span className={cn("relative z-[2] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border", listed ? "border-transparent bg-lavender brand-tint text-white" : "border-hairline bg-surface text-ink/60")}>
            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
          <span className="min-w-0 flex-1 flex items-baseline gap-2.5 pt-1.5">
            <span className={cn("shrink-0 w-[42px] font-mono text-[10.5px] tabular-nums", listed ? "text-ink/55" : "text-ink/40")}>{t("l11.pb.tl.day", { n: day })}</span>
            <span className={cn("min-w-0 text-[13px] leading-[1.35]", listed ? "font-medium text-ink" : "text-ink/65")}>{t(`l11.pb.tl.${tone}.${k}`)}</span>
          </span>
        </motion.li>
      ))}
      {end && (
        <motion.li variants={labelV(0.5)} className="relative flex items-center gap-3 py-2.5">
          <span aria-hidden className="h-8 w-8 shrink-0" />
          <span className={cn("inline-flex items-center rounded-full px-2.5 h-7 text-[12px] font-medium", listed ? "bg-[var(--accent-soft)] text-ink" : "bg-fill text-ink/60")}>{end}</span>
        </motion.li>
      )}
    </ol>
  );
}

function Track({ tone, items, end, figure, figureSub, meta }) {
  const listed = tone === "listed";
  const last = items[items.length - 1].day / DAYS;
  return (
    <div className="grid gap-y-4 lg:grid-cols-[150px_minmax(0,1fr)_170px] lg:items-center lg:gap-x-8" data-testid={`problem-track-${tone}`}>
      <div className="min-w-0 lg:self-start lg:pt-14">
        <span className={cn("inline-flex items-center gap-2 h-7 rounded-full border pl-2.5 pr-3 text-[12px] font-medium", listed ? "border-transparent bg-[var(--accent-soft)] text-ink" : "border-hairline bg-surface text-ink/75")}>
          <span className={cn("h-1.5 w-1.5 rounded-full", listed ? "bg-lavender brand-tint" : "bg-warn")} />
          {t(listed ? "l11.pb.listed" : "l11.pb.today")}
        </span>
        <p className="mt-2 text-[12.5px] text-ink/50 leading-snug">{meta}</p>
      </div>
      <div className="hidden lg:block min-w-0 px-[70px]">
        <Rail items={items} tone={tone} end={end} testId={`problem-rail-${tone}`} />
      </div>
      <div className="lg:hidden min-w-0">
        <RailStack items={items} tone={tone} end={end} testId={`problem-rail-${tone}`} />
      </div>
      <motion.div variants={figureV(last)} className="min-w-0 lg:text-right will-change-transform" data-testid={`problem-figure-${tone}`}>
        <p className={cn("font-display text-[36px] sm:text-[48px] leading-none tracking-[-0.03em]", listed ? "text-ink" : "text-ink/45")}>{figure}</p>
        <p className="mt-2 text-[13px] text-ink/55 leading-snug">{figureSub}</p>
      </motion.div>
    </div>
  );
}

export function ProblemSection({ container }) {
  const reduced = useReducedMotion();
  return (
    <section id="problem" className="mt-14 sm:mt-28 border-t border-hairline scroll-mt-16 overflow-x-clip" data-testid="problem-section">
      <div className={cn(container, "py-16 sm:py-28")}>
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal as="p" className={EYEBROW}>{t("l11.pb.eyebrow")}</Reveal>
            <Reveal as="h2" delay={0.04} className={cn(H2, "max-w-[20ch]")} data-testid="problem-title">{t("l11.pb.title")}</Reveal>
          </div>
          <Reveal as="p" delay={0.08} className={cn(SUB, "lg:col-span-5 lg:mt-0 lg:pb-2 max-w-[50ch]")}>{tb("l11.pb.sub")}</Reveal>
        </div>

        {/* ---- the same week, as time ---- */}
        <motion.div initial={reduced ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.3 }} className="relative mt-10 sm:mt-16 overflow-hidden rounded-[24px] sm:rounded-[36px] border border-hairline bg-surface shadow-float" data-testid="problem-compare">
          <span aria-hidden className="pointer-events-none hidden lg:block absolute -top-48 right-[-10%] h-[440px] w-[560px] rounded-full opacity-60" style={{ background: "radial-gradient(closest-side, var(--accent-glow), transparent 72%)", filter: "blur(40px)" }} />
          <div className="relative p-5 sm:p-8 lg:p-12 grid gap-8 lg:gap-8">
            <Track tone="today" items={TODAY} end={t("l11.pb.tl.today.end")} figure={t("l11.pb.tl.today.figure")} figureSub={t("l11.pb.tl.today.figure_sub")} meta={t("l11.pb.today_meta")} />
            <div className="border-t border-hairline lg:mx-[-8px]" aria-hidden />
            <Track tone="listed" items={LISTED} end={t("l11.pb.tl.listed.end")} figure={t("l11.pb.tl.listed.figure")} figureSub={tb("l11.pb.tl.listed.figure_sub")} meta={t("l11.pb.listed_meta")} />
          </div>
        </motion.div>

        {/* ---- three proof points ---- */}
        <div className="mt-10 sm:mt-16 grid sm:grid-cols-3 gap-x-8 gap-y-7" data-testid="problem-proof">
          {PROOF.map(({ n, icon: Icon }, i) => (
            <Reveal key={n} delay={0.06 + i * 0.07} y={14} className="min-w-0 sm:border-l sm:border-hairline sm:pl-6 first:border-0 first:pl-0" data-testid={`problem-proof-${n}`}>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-[11px] bg-[var(--accent-soft)] text-lavender brand-tint"><Icon className="h-4 w-4" strokeWidth={1.9} /></span>
              <h3 className="mt-4 font-heading font-bold text-[18px] sm:text-[19px] tracking-[-0.02em] text-ink leading-tight text-balance">{t(`l11.pb.pp.${n}.t`)}</h3>
              <p className="mt-2 text-[14.5px] text-ink/60 leading-[1.6] max-w-[36ch]">{tb(`l11.pb.pp.${n}.p`)}</p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-8 sm:mt-10 flex justify-center sm:justify-end">
          <a href="#capabilities" className="inline-flex items-center gap-1.5 text-[14.5px] font-medium text-ink hover:text-lavender transition-colors" data-testid="problem-cta">
            {t("l11.pb.cta")} <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
